/** Mirrored by the website runtime. Unedited legacy headers retain their original source order. */
export type HeaderLogoRole = "primary" | "alternative";
export type HeaderLogoSource =
  | "header"
  | "website"
  | "media"
  | "context"
  | "missing";
export interface HeaderLogo {
  role: HeaderLogoRole;
  url?: string;
  source: HeaderLogoSource;
  name: string;
}
export interface HeaderLogoMedia {
  logo?: string;
  inverseLogo?: string;
}
type RecordValue = Record<string, any>;
export interface HeaderLogoInput {
  website: RecordValue;
  config: RecordValue;
  definition?: { logoUrl?: string; inverseLogoUrl?: string; colours?: unknown };
  media?: HeaderLogoMedia;
}
export const HEADER_LOGO_LABELS = {
  primary: "Primært logo",
  alternative: "Alternativt logo",
};
export function logoFileName(url?: string): string {
  if (!url) return "Intet logo";
  try {
    const parts = new URL(url, "https://example.invalid").pathname
      .split("/")
      .filter(Boolean);
    const last = parts.pop() ?? "";
    return decodeURIComponent(
      /^(xs|sm|md|lg|xl|original)$/i.test(last) ? (parts.pop() ?? last) : last,
    );
  } catch {
    return "Logo";
  }
}
export function resolveHeaderLogos(
  input: HeaderLogoInput,
): Record<HeaderLogoRole, HeaderLogo> {
  const { website, config, definition, media = {} } = input;
  const shared =
    website.logoSourceMode === "shared" ||
    config.logoSourceMode === "shared" ||
    !!definition;
  const mediaFirst =
    shared ||
    ["DefaultHeader", "TransparentHeader"].includes(
      config.type ?? "DefaultHeader",
    );
  const make = (role: HeaderLogoRole): HeaderLogo => {
    const key = role === "primary" ? "logoUrl" : "inverseLogoUrl";
    const standard = role === "primary" ? media.logo : media.inverseLogo;
    const ownValue = definition?.[key] || config.logoOverrides?.[role];
    const own = typeof ownValue === "string" && ownValue ? ownValue : undefined;
    const candidates: [string | undefined, HeaderLogoSource][] = [
      [own, "header"],
      ...((mediaFirst
        ? [
            [standard, "media"],
            [website[key], "website"],
          ]
        : [
            [website[key], "website"],
            [config.type === "ClassicHeader" ? undefined : standard, "media"],
          ]) as [string | undefined, HeaderLogoSource][]),
    ];
    const found = candidates.find(
      ([url]) => typeof url === "string" && url.length > 0,
    );
    return {
      role,
      url: found?.[0],
      source: found?.[1] ?? "missing",
      name: logoFileName(found?.[0]),
    };
  };
  return { primary: make("primary"), alternative: make("alternative") };
}
export function headerLogoForState(
  input: HeaderLogoInput,
  state: {
    scrolled?: boolean;
    mobile?: boolean;
    menuOpen?: boolean;
    light?: boolean;
  } = {},
): { logo: HeaderLogo; fallback: boolean; hidden: boolean; reason?: string } {
  const logos = resolveHeaderLogos(input),
    c = input.config,
    kind = c.type ?? "DefaultHeader";
  let role: HeaderLogoRole = "primary";
  if (kind === "TransparentHeader")
    role = state.scrolled || c.isVisible ? "alternative" : "primary";
  if (kind === "FloatingHeader") role = state.light ? "alternative" : "primary";
  if (kind === "ClassicHeader") {
    const overlay = c.contentPlacement === "overlay";
    if (state.menuOpen || (overlay && !state.scrolled)) role = "alternative";
    const special = state.menuOpen
      ? c.classic?.menuLogoUrl
      : role === "alternative"
        ? c.classic?.overlayLogoUrl
        : undefined;
    if (special)
      return {
        logo: {
          role,
          url: special,
          source: "context",
          name: logoFileName(special),
        },
        fallback: false,
        hidden: false,
      };
  }
  // Existing Standard/Transparent/Floating require a primary source before showing either image.
  const hidden =
    (kind !== "ClassicHeader" && !logos.primary.url) ||
    (kind === "FloatingHeader" && c.floating?.logoMode === "text") ||
    (kind === "TransparentHeader" && !!state.mobile && !!state.menuOpen);
  const selected = logos[role],
    fallback = !selected.url && role === "alternative" && !!logos.primary.url;
  return {
    logo: fallback ? logos.primary : selected,
    fallback,
    hidden,
    reason: hidden
      ? !logos.primary.url
        ? "Det primære logo mangler."
        : "Denne headertype viser ikke et logo i den valgte tilstand."
      : undefined,
  };
}
const spacing: Record<string, number> = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 32,
  xl: 48,
  "2xl": 96,
};
export function logoPadding(
  value: unknown,
  mobile: boolean,
  fallback: number,
): [number, number, number, number] {
  const halves = String(value ?? "")
    .split("|")
    .map((x) => x.trim());
  const tokens = (mobile ? halves[0] : (halves[1] ?? halves[0]))
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length || tokens.length > 4) return [fallback, 0, fallback, 0];
  const expanded =
    tokens.length === 1
      ? [tokens[0], tokens[0], tokens[0], tokens[0]]
      : tokens.length === 4
        ? tokens
        : [tokens[0], tokens[1], tokens[0], tokens[1]];
  return expanded.map(
    (token, index) => spacing[token] ?? (index % 2 === 0 ? fallback : 0),
  ) as [number, number, number, number];
}
function dimension(value: unknown, fallback: string, mobile: boolean): string {
  if (typeof value !== "string" && typeof value !== "number") return fallback;
  const halves = String(value)
    .split("|")
    .map((x) => x.trim());
  const v = (mobile ? halves[0] : (halves[1] ?? halves[0])) || fallback;
  return /^\d+(\.\d+)?$/.test(v) ? `${v}px` : v;
}
export function headerLogoGeometry(
  config: RecordValue,
  mobile: boolean,
  scrolled: boolean,
) {
  const kind = config.type ?? "DefaultHeader";
  const visible = scrolled || !!config.isVisible;
  let height = dimension(
    mobile ? config.heightMobile : config.heightDesktop,
    kind === "TransparentHeader"
      ? dimension(config.heightMobile, "80px", true)
      : mobile
        ? "80px"
        : "100px",
    mobile,
  );
  if (kind === "TransparentHeader" && visible)
    height = dimension(
      mobile ? config.heightMobileVisible : config.heightDesktopVisible,
      dimension(config.heightMobileVisible, "60px", mobile),
      mobile,
    );
  const explicit = kind === "FloatingHeader" || kind === "ClassicHeader";
  if (explicit)
    height = dimension(
      kind === "FloatingHeader"
        ? config.floating?.logoHeight
        : config.classic?.logoHeight,
      kind === "FloatingHeader" ? "24px" : "90px",
      mobile,
    );
  const padding = logoPadding(
    kind === "TransparentHeader"
      ? config.paddingTransparent
      : config.paddingDefault,
    mobile,
    explicit ? 0 : 16,
  );
  return {
    height,
    barHeight:
      kind === "ClassicHeader"
        ? "116px"
        : kind === "FloatingHeader"
          ? dimension(config.floating?.height, "58px", mobile)
          : height,
    padding: explicit
      ? ([0, 0, 0, 0] as [number, number, number, number])
      : padding,
    outerPadding:
      kind === "TransparentHeader"
        ? logoPadding(config.padding, mobile, 0)
        : ([0, 0, 0, 0] as [number, number, number, number]),
    explicit,
    centered:
      ["DefaultHeader", "TransparentHeader"].includes(kind) &&
      !mobile &&
      config.logoPlacement === "center",
  };
}
